import { useCallback, useEffect, useMemo, useState } from 'react';

import { matchPath, useLocation } from '@/providers/RouterShim';
import useAlertBanners, {
  type AlertBanner as AlertBannerType,
} from '../../data/alertBanner/useAlertBanners';
import IncidentAlert from '../../styled-components/components/IncidentAlert';

/*
// simple, not nested
const splitPath = (path: string): string[] => {
  const regex = /\(([^)]+)\)/g;
  let paths = [path];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(path)) !== null) {
    const options = match[1].split('|');
    paths = paths.flatMap((p) => options.map((option) => p.replace(match?.[0]!, option)));
  }

  return paths;
};
*/
const splitPath = (path: string): string[] => {
  const expand = (p: string): string[] => {
    const regex = /\(([^()]+)\)/g;
    let match: RegExpExecArray | null;
    let paths = [p];

    while ((match = regex.exec(p)) !== null) {
      const options = match[1].split('|');
      paths = paths.flatMap((path) => options.map((option) => path.replace(match?.[0]!, option)));
    }

    return paths;
  };

  let result = new Set([path]);
  let prevLength = 0;

  // Repeat until no further expansion occurs
  while (prevLength !== result.size) {
    prevLength = result.size;
    result = new Set(Array.from(result).flatMap(expand));
  }

  return Array.from(result);
};

const localStorageKey = 'alertBannersSeen';

const getAlertBannerSeenList = (): string[] => {
  const seenList = localStorage.getItem(localStorageKey);
  if (!seenList) return [];
  try {
    const parsed = JSON.parse(seenList);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) {
    return [];
  }
};

const setAlertBannerSeenList = (id: string) => {
  const list = getAlertBannerSeenList();
  const nextList = [...new Set([...list, id])];
  localStorage.setItem(localStorageKey, JSON.stringify(nextList));
};

const canShow = (id: string) => !getAlertBannerSeenList().includes(id);

const resolveBanner = (
  pathname: string,
  banners?: AlertBannerType[],
): AlertBannerType[] | undefined => {
  // Removes already seen or expired banners
  const filteredBanners = banners?.filter((b) => {
    return (b.expire ? new Date().valueOf() < new Date(b.expire).valueOf() : true) && canShow(b.id);
  });

  //Extract routes and check if matching
  const matchedBanners = filteredBanners?.filter((b) => {
    return b.path ? splitPath(b.path).some((p) => matchPath(p, pathname)) : true;
  });
  return matchedBanners;
};

const useVisibleAlertBanner = (): [AlertBannerType[] | undefined, (id: string) => void] => {
  const { pathname } = useLocation();
  const [alertBanners] = useAlertBanners();
  const [banners, setBanners] = useState<AlertBannerType[] | undefined>();

  const filteredBanners = useMemo(
    () => resolveBanner(pathname, alertBanners),
    [alertBanners, pathname],
  );

  useEffect(() => {
    setBanners(filteredBanners);
  }, [filteredBanners]);

  const onRemove = useCallback(
    (id: string) => {
      if (alertBanners) {
        setAlertBannerSeenList(id);
        setBanners((s) => s?.filter((x) => x.id !== id));
      }
    },
    [alertBanners],
  );

  return [banners, onRemove];
};

const AlertBanner = () => {
  const [alertBanners, onRemove] = useVisibleAlertBanner();

  if (!alertBanners?.length) return null;

  return <IncidentAlert banners={alertBanners} onRemove={onRemove} />;
};

export default AlertBanner;
